<template>
  <base-drawer
    class="audience-drawer"
    :show="show"
    title="Edit saved audience"
    width="640px"
    @close="onClose"
  >
    <drawer-section title="Details" icon="icon-notes">
      <base-input label="Audience name" class="mb-0">
        <el-input v-model="name" placeholder="e.g. Birthday customers" maxlength="255" />
      </base-input>
    </drawer-section>

    <drawer-section title="Who is included" icon="icon-single-02">
      <info-hint>
        Campaigns that already use this audience keep the recipients they were saved with. Changes here apply to campaigns created after saving.
      </info-hint>
      <!-- CustomerFilterPanel seeds its state once at setup, so remount per audience -->
      <customer-filter-panel
        v-if="show && audience"
        :key="audience.id"
        class="mt-3"
        :value="filter"
        :games="games"
        :scoped-group-ids="scopedGroupIds"
        @input="filter = $event"
      />
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="saving" @click="save">
          <i v-if="saving" class="el-icon-loading mr-1"></i>Save
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, getCurrentInstance, type PropType } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import CustomerFilterPanel from '@/components/CustomerFilterPanel.vue'

const props = defineProps({
  show: { type: Boolean, default: false },
  audience: { type: Object as PropType<any>, default: null },
  games: { type: Array as PropType<any[]>, default: () => [] },
  scopedGroupIds: { type: Array as PropType<number[] | null>, default: null }
})

const emit = defineEmits(['update:show', 'close', 'saved'])

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const name = ref('')
const filter = ref<Record<string, any>>({})
const saving = ref(false)

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function apiErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data
  if (data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data
      if (Array.isArray(parsed?.validation_errors) && parsed.validation_errors.length > 0) {
        return parsed.validation_errors[0].message
      }
      if (parsed?.error) return parsed.error
    } catch {
      if (typeof data === 'string' && data.length < 200) return data
    }
  }
  return fallback
}

function loadForm() {
  name.value = props.audience?.name || ''
  filter.value = { ...(props.audience?.filter_json || {}) }
}

async function save() {
  if (!props.audience) return
  if (!name.value.trim()) {
    proxy.$notify({ type: 'warning', message: 'Give the audience a name', icon: 'tim-icons icon-alert-circle-exc' })
    return
  }
  saving.value = true
  try {
    const config = await authConfig()
    const payload = { name: name.value.trim(), filter_json: filter.value }
    const response = await axios.put(`${baseUrl}/customer-audiences/${props.audience.id}`, payload, config)
    proxy.$notify({ type: 'success', message: 'Audience saved', icon: 'tim-icons icon-check-2' })
    // The server may clamp a restricted caller's filter to their groups; keep
    // the stored version so the list and the drawer show what was saved.
    emit('saved', { ...props.audience, name: payload.name, filter_json: response.data?.filter_json || payload.filter_json })
    emit('update:show', false)
    emit('close')
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: apiErrorMessage(err, 'The audience could not be saved'), icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    saving.value = false
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    loadForm()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>

<script lang="ts">
export default { name: 'audience-drawer' }
</script>

<style lang="scss" scoped>
.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
